/**
 * Shared scaffolding for every widget tool's `{ …, data }` argument normalization — the parts that
 * are identical no matter which widget the tool creates or updates:
 *
 * - `data` sometimes arrives as a JSON-encoded string (leaked-function-calls XML params always
 *   arrive as raw strings) — parsed here so the object schema sees a real object.
 * - A present-but-malformed `data` (unparsable string, array, number) must FAIL validation — a
 *   `: {}` fallback would mask it as "no data" and turn the call into a silent no-op/default
 *   that reports success, which drove the model into retry loops when observed live.
 * - Flat top-level synonyms the fold has already consumed (`flatSynonymKeys`) are stripped, so they
 *   never round-trip as unrecognized noise.
 *
 * What differs per tool is the fold itself, injected as `foldData`: button's create wraps a flat
 * label INTO `{ buttons: [{ label }] }`, its update unwraps that same wrapper back into a flat
 * partial-item patch, text's folds `text`/`html` into `content` and normalizes alignment synonyms.
 * `foldData` may mutate `args`/`data` and must return the data object to use (allowing wholesale
 * replacement).
 *
 * Runs via `z.preprocess()` — BEFORE schema validation, so it works identically regardless of
 * transport: the AI SDK chat path (native tool-calling or the leaked-function-calls text-emulation
 * path), or a real MCP client (in `execute()`/a handler it would be too late: a plain `z.object()`
 * strips unrecognized keys silently).
 */
export function normalizeWidgetArgs(
    raw: unknown,
    foldData: (args: Record<string, unknown>, data: Record<string, unknown>) => Record<string, unknown>,
    flatSynonymKeys: readonly string[]
): unknown {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
        return raw;
    }

    const args = { ...(raw as Record<string, unknown>) };

    if (typeof args['data'] === 'string') {
        try {
            args['data'] = JSON.parse(args['data']);
        } catch {
            // leave the unparsable string in place so validation rejects it loudly
        }
    }

    if (args['data'] != null && (typeof args['data'] !== 'object' || Array.isArray(args['data']))) {
        return args;
    }

    const data: Record<string, unknown> = args['data'] ? { ...(args['data'] as Record<string, unknown>) } : {};

    args['data'] = foldData(args, data);

    for (const key of flatSynonymKeys) {
        delete args[key];
    }

    return args;
}
